"use client";

import React, { createContext, useContext, useEffect, useState } from "react";

import { applyThemeMode, readStoredThemeMode, type ThemeMode } from "./theme";

interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used within <ThemeProvider>");
  return ctx;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>("system");

  // Apply the persisted mode once on mount (server always renders without
  // .dark — no next-themes blocking script, so there is a brief flash on
  // first paint if the stored mode is "dark"; acceptable for this pass).
  useEffect(() => {
    const stored = readStoredThemeMode();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setModeState(stored);
    applyThemeMode(document.documentElement, stored);
  }, []);

  // Re-resolve on OS theme change while in "system" mode.
  useEffect(() => {
    if (mode !== "system" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => applyThemeMode(document.documentElement, "system");
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, [mode]);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    applyThemeMode(document.documentElement, next);
  };

  return <ThemeContext.Provider value={{ mode, setMode }}>{children}</ThemeContext.Provider>;
}
